import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Autocomplete,
  Button,
  ButtonGroup,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TableContainer,
  TextField,
} from '@mui/material';

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';

import IPage from '../../interfaces/IPage';
import { useOrderService } from '../../services/useOrderService';
import { useToastr } from '../../hooks/useToastr';
import { useAuth } from '../../hooks/auth';
import { IOrder } from '../../interfaces/IOrder';
import { useOrderStatusService } from '../../services/useOrderStatusService';
import { useNavigate } from 'react-router-dom';
import { IRequestPostParams } from '../../interfaces/IRequestPostParams';
import { TitleContainer } from '../../components/title-container/TitleContainer';
import { PageCard } from '../../components/page-card/PageCard';
import { IEvent } from '../../interfaces/IEvent';
import { FilterArea, TableBodyCard } from './OrdersList.styled';
import OrderCard from './order-card/OrderCard';
import CustomTablePagination from '../../components/table/CustomTablePagination/CustomTablePagination';
import { BackdropCustom } from '../../components/backdrop/Backdrop';
import OrderConfirmDialog from './order-confirm-dialog/OrderConfirmDialog';
import OrderCloneDialog from './order-clone-dialog/OrderConfirmDialog';

// Constante para as chaves do sessionStorage
const FILTER_STORAGE_KEY = '@PEDIDO-WEB:orderList';

const fields = [
  {
    field: 'branchOfficeCode',
    label: 'Filial',
  },
  {
    field: 'protocolNumber',
    label: 'Orçamento',
  },
  {
    field: 'orderNumber',
    label: 'Número do Pedido',
  },
  {
    field: 'customerCode',
    label: 'Cód. Cliente',
  },
  {
    field: 'customerTradeName',
    label: 'Cliente',
  },
  {
    field: 'sellerCode',
    label: 'Cód. Representante',
  },
  {
    field: 'sellerName',
    label: 'Representante',
  },
  {
    field: 'hubSpotId',
    label: 'ID Negociação HubSpot',
  },
];

interface IParams {
  perPage: number;
  currentPage: number;
  orderField: string;
  orderDirection: 'asc' | 'desc';
  field?: string;
  value?: string;
  precision?: string;
  delay?: number;
}

interface IOrderStatus {
  id: number;
  description: string;
}

const OrdersList: React.FC<IPage> = ({ title }) => {
  window.document.title = title;

  const { listAll, cloneOrder } = useOrderService();

  const toastr = useToastr();
  const timeout = useRef<any>(null);
  const { state, orderParams } = useAuth();

  const [orders, setOrders] = useState<IOrder[]>([]);
  const [filterAreaOpen, setFilterAreaOpen] = useState(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue ? true : false;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue ? JSON.parse(savedValue).perPage : 10;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue ? JSON.parse(savedValue).currentPage : 0;
  });
  const [orderDirection] = useState<'asc' | 'desc'>(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue ? JSON.parse(savedValue).orderDirection : 'asc';
  });
  const [orderField, setOrderField] = useState<string>(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue ? JSON.parse(savedValue).orderField : 'orderNumber';
  });
  const [loading, setLoading] = useState(false);
  const [clonedOrderId, setClonedOrderId] = useState('');

  const [filterField, setFilterField] = useState<string>(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue ? JSON.parse(savedValue).filterField : 'sellerCode';
  });
  const [filterPrecision, setFilterPrecision] = useState<string>(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue ? JSON.parse(savedValue).filterPrecision : 'equal';
  });

  const [selectedOrderStatus, setSelectedOrderStatus] = useState<IOrderStatus[]>(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue ? JSON.parse(savedValue).selectedOrderStatus : [];
  });

  const [orderStatus, setOrderStatus] = useState<IOrderStatus[] | []>([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [initialDate, setInitialDate] = useState<Dayjs | null>(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue && dayjs(JSON.parse(savedValue).initialDate).isValid()
      ? dayjs(JSON.parse(savedValue).initialDate)
      : null;
  });
  const [finalDate, setFinalDate] = useState<Dayjs | null>(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return savedValue && dayjs(JSON.parse(savedValue).finalDate).isValid()
      ? dayjs(JSON.parse(savedValue).finalDate)
      : null;
  });
  const [originalOrderId, setOriginalOrderId] = useState<string | null>(null);
  const [originalOrderTypeId, setOriginalOrderTypeId] = useState<number | null>(null);

  const { listOrderStatus } = useOrderStatusService();

  const [filterValue, setFilterValue] = useState<string>(() => {
    const savedValue = sessionStorage.getItem(FILTER_STORAGE_KEY);

    return savedValue ? JSON.parse(savedValue).filterValue : String(state.seller?.code || '');
  });
  const [delay, setDelay] = useState(0);

  const navigate = useNavigate();

  const saveFiltersToSession = useCallback(() => {
    const filters = {
      filterField,
      filterValue,
      filterPrecision,
      selectedOrderStatus,
      orderField,
      filterAreaOpen,
      initialDate: initialDate ? initialDate.format('YYYY-MM-DD') : null,
      finalDate: finalDate ? finalDate.format('YYYY-MM-DD') : null,
      perPage,
      currentPage,
    };

    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [
    filterField,
    filterValue,
    filterPrecision,
    selectedOrderStatus,
    orderField,
    filterAreaOpen,
    initialDate,
    finalDate,
    perPage,
    currentPage,
  ]);

  const setFilterFieldWithStorage = useCallback(
    (value: string) => {
      setFilterField(value);
      saveFiltersToSession();
    },
    [saveFiltersToSession],
  );

  const setFilterValueWithStorage = useCallback(
    (value: string) => {
      setFilterValue(value);
      saveFiltersToSession();
    },
    [saveFiltersToSession],
  );

  const setFilterPrecisionWithStorage = useCallback(
    (value: string) => {
      setFilterPrecision(value);
      saveFiltersToSession();
    },
    [saveFiltersToSession],
  );

  const setOrderFieldWithStorage = useCallback(
    (value: string) => {
      setOrderField(value);
      saveFiltersToSession();
    },
    [saveFiltersToSession],
  );

  const setFilterAreaOpenWithStorage = useCallback(
    (value: boolean) => {
      setFilterAreaOpen(value);
      saveFiltersToSession();
    },
    [saveFiltersToSession],
  );

  const setSelectedOrderStatusWithStorage = useCallback(
    (values: IOrderStatus[]) => {
      setSelectedOrderStatus(values);
      saveFiltersToSession();
    },
    [saveFiltersToSession],
  );

  const setInitialDateWithStorage = useCallback(
    (value: Dayjs | null) => {
      setInitialDate(value);
      saveFiltersToSession();
    },
    [saveFiltersToSession],
  );

  const setFinalDateWithStorage = useCallback(
    (value: Dayjs | null) => {
      setFinalDate(value);
      saveFiltersToSession();
    },
    [saveFiltersToSession],
  );

  const handlePagination = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: 'asc' | 'desc',
  ) => {
    handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay: 0,
    });
  };

  useEffect(() => {
    saveFiltersToSession();
  }, [
    filterField,
    filterValue,
    filterPrecision,
    selectedOrderStatus,
    orderField,
    filterAreaOpen,
    initialDate,
    finalDate,
    saveFiltersToSession,
  ]);

  const handleToEdit = useCallback(
    (id: string | undefined) => {
      !!id ? navigate(`/order/${id}/edit`) : navigate('/order/steper/0}/new');
    },
    [navigate],
  );

  const handleListAll = useCallback(
    async ({ perPage, currentPage, orderField, field, value, precision, delay }: IParams) => {
      const filter: IRequestPostParams = {
        perPage,
        currentPage: currentPage ? currentPage + 1 : 1,
        orderBy: orderField,
        orderDirection: 'desc',
        filterField: `${field}`,
        filterValue: `${value}`,
        precision: `${precision}`,
        status: selectedOrderStatus.map(s => s.id),
        initialDate: initialDate?.format('YYYY-MM-DD') || null,
        finalDate: finalDate?.format('YYYY-MM-DD') || null,
      };

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoading(true);
        await listAll(filter)
          .then(response => {
            if (response?.data?.length > 0) {
              setOrders(response.data);
              setTotalRows(response.totalRows);
              setLoading(false);
            } else {
              setOrders([]);
              setTotalRows(0);
              setPerPage(10);
              setCurrentPage(0);
              setLoading(false);
            }
          })
          .catch(error => {
            toastr.error(error?.message || 'Contate a equipe de suporte');
          })
          .finally(() => {
            setLoading(false);
          });
      }, delay || 0);
    },
    [selectedOrderStatus, initialDate, finalDate, timeout],
  );

  const handleReturnError = (events: IEvent[] | undefined): string => {
    if (events) {
      const eventsSorted = events.sort((a, b) => {
        if (a.created_at < b.created_at) {
          return -1;
        }
        if (a.created_at > b.created_at) {
          return 1;
        }
        return 0;
      });

      const lestEvent = eventsSorted[eventsSorted.length - 1];

      return lestEvent.message;
    } else {
      return '';
    }
  };

  useEffect(() => {
    handlePagination(perPage, 0, orderField, 'asc');
  }, [orderField, selectedOrderStatus]);

  useEffect(() => {
    const field = filterField;
    const value = filterValue;
    const precision = filterPrecision;

    handleListAll({ perPage, currentPage, orderField, orderDirection, field, value, precision, delay });
  }, [filterField, filterPrecision, filterValue, initialDate, finalDate]);

  const handleTextFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValueWithStorage(e.target.value);
    setTotalRows(0);
    setCurrentPage(0);
    setDelay(500);
  };

  const handleViewOrder = useCallback(
    async (orderId: string | undefined) => {
      if (orderId && orderId.length > 15) {
        const token = btoa(state.tenantDomain);

        if (orderParams?.reportDefaultFormat === 'PDF') {
          window.open(
            `${process.env.REACT_APP_BASE_FRONTEND_URL}/#/order/${orderId}/report/pdf?token=${token}`,
            '_blank',
          );
        } else {
          window.open(`${process.env.REACT_APP_BASE_FRONTEND_URL}/#/order/${orderId}/report?token=${token}`, '_blank');
        }
      }
    },
    [orderParams],
  );

  const handleChangeOrderStatus = (event: React.ChangeEvent<{}>, values: IOrderStatus[]) => {
    setCurrentPage(0);
    setSelectedOrderStatusWithStorage(values);
  };

  const handleListOrderStatus = useCallback(async () => {
    setLoading(true);
    await listOrderStatus(`perPage=100&currentPage=1&orderBy=id&orderDirection=asc`)
      .then(response => {
        const responseErpStatusFiltered = response.data.filter(
          responseItem =>
            !selectedOrderStatus.some(selectedItem => (selectedItem.id === responseItem.id ? true : false)),
        );

        const responseErpStatus = responseErpStatusFiltered.map(status => {
          return {
            id: status.id,
            description: status.description ? status.description : '',
          };
        });

        setOrderStatus(responseErpStatus);
      })
      .catch(error => {
        toastr.error(error?.message || 'Contate a equipe de suporte');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedOrderStatus]);

  const handleConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    navigate(`/order/${clonedOrderId}/edit?recalculate=true`);
  };

  const handleDenyDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  useEffect(() => {
    handleListOrderStatus();
  }, []);

  const handleCloneOrder = async (
    orderId: string | undefined,
    keepItems: boolean,
    hubSpotId: string,
    newCustomerId: string,
  ): Promise<void> => {
    setIsCloneDialogOpen(false);
    setLoading(true);
    await cloneOrder(`${orderId}`, keepItems, hubSpotId, newCustomerId)
      .then(newOrder => {
        setClonedOrderId(`${newOrder?.id}`);
        setIsConfirmDialogOpen(true);
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
    await handleListAll({
      perPage,
      currentPage,
      orderField,
      orderDirection,
      field: filterField,
      value: filterValue,
      precision: filterPrecision,
      delay,
    });
  };

  const handleClearFilters = () => {
    setFilterField('sellerCode');
    setFilterValue(state.seller?.code || '');
    setFilterPrecision('equal');
    setSelectedOrderStatus([]);
    setInitialDate(null);
    setFinalDate(null);
    setOrderField('orderNumber');
    setPerPage(10);
    setCurrentPage(0);

    // Limpa os filtros no sessionStorage
    sessionStorage.removeItem(FILTER_STORAGE_KEY);

    // Recarrega a lista com os valores padrão
    handleListAll({
      perPage: 10,
      currentPage: 0,
      orderField: 'orderNumber',
      orderDirection: 'asc',
      field: 'sellerCode',
      value: '',
      precision: 'equal',
      delay: 0,
    });
  };

  useEffect(() => {
    handleListOrderStatus();
  }, [selectedOrderStatus]);

  return (
    <>
      <TitleContainer>
        <h1>Pedidos</h1>
      </TitleContainer>
      <PageCard>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '10px 0px 10px 10px',
          }}
        >
          <ButtonGroup>
            <IconButton
              onClick={() => navigate('/order/steper/0/new')}
              size="small"
              color="primary"
              style={{ alignItems: 'flex-end' }}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              onClick={() => handlePagination(perPage, currentPage, orderField, orderDirection)}
              size="small"
              color="primary"
              style={{ alignItems: 'flex-end' }}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              onClick={() => setFilterAreaOpenWithStorage(!filterAreaOpen)}
              size="small"
              color="primary"
              style={{ alignItems: 'flex-end' }}
            >
              {filterAreaOpen ? <FilterAltOffOutlinedIcon /> : <FilterAltOutlinedIcon />}
            </IconButton>
          </ButtonGroup>
        </div>
        {filterAreaOpen && (
          <FilterArea container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-filter">Filtrar por</InputLabel>
                <Select
                  label="Filtrar Por"
                  value={`${filterField}`}
                  onChange={e => {
                    setFilterFieldWithStorage(`${e.target.value}`);
                  }}
                >
                  {fields
                    .filter(f => {
                      if (!orderParams?.integrateHubSpot) {
                        return f.field !== 'hubSpotId';
                      }

                      return true;
                    })
                    .map(filter => {
                      return (
                        <MenuItem key={filter.field} value={filter.field}>
                          {filter.label}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Precisão</InputLabel>
                <Select
                  label="Precisão"
                  value={`${filterPrecision}`}
                  onChange={e => {
                    setFilterPrecisionWithStorage(`${e.target.value}`);
                  }}
                >
                  <MenuItem key={'equal'} value={'equal'}>
                    Igual
                  </MenuItem>
                  <MenuItem key={'containing'} value={'containing'}>
                    Contendo
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Valor a filtrar"
                placeholder="Valor a filtrar"
                value={filterValue}
                onChange={handleTextFilterChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                style={{ width: '100%' }}
                multiple
                id="tags-outlined"
                options={orderStatus}
                getOptionLabel={option => option.description}
                filterSelectedOptions
                size="small"
                value={selectedOrderStatus}
                onChange={handleChangeOrderStatus}
                renderInput={params => {
                  return <TextField {...params} label="Status do pedido" />;
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <DatePicker
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  label="Data Inicial"
                  value={initialDate}
                  onChange={value => setInitialDateWithStorage(dayjs(String(value)))}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <DatePicker
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  label="Data Final"
                  value={finalDate}
                  onChange={value => setFinalDateWithStorage(dayjs(String(value)))}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl size="small" fullWidth>
                <InputLabel id="demo-simple-select-label">Ordenar por</InputLabel>
                <Select
                  value={orderField}
                  onChange={e => {
                    setOrderFieldWithStorage(`${e.target.value}`);
                  }}
                  label="Ordenar por"
                >
                  <MenuItem key="orderNumber" value="orderNumber">
                    Status e Número do Pedido
                  </MenuItem>
                  <MenuItem key="orderDate  " value="orderDate">
                    Status e Data Emissão
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" sx={{ textTransform: 'none' }} onClick={handleClearFilters}>
                Limpar Filtros
              </Button>
            </Grid>
          </FilterArea>
        )}

        {filterAreaOpen && <div style={{ width: '100%', height: '24px' }}></div>}

        <TableContainer>
          <TableBodyCard>
            {orders &&
              orders.map(order => {
                return (
                  <OrderCard
                    orderParams={orderParams}
                    key={order.id}
                    order={order}
                    handleReturnError={handleReturnError}
                    handleViewOrder={handleViewOrder}
                    handleToEdit={handleToEdit}
                    listAll={() => {
                      handleListAll({
                        perPage,
                        currentPage,
                        orderField,
                        orderDirection,
                        field: filterField,
                        value: filterValue,
                        precision: filterPrecision,
                        delay: 0,
                      });
                    }}
                    handleCloneOrder={() => {
                      if (order.id) {
                        setOriginalOrderId(order.id);
                        setOriginalOrderTypeId(order.orderTypeId || null);
                        setIsCloneDialogOpen(true);
                      }
                    }}
                  />
                );
              })}
          </TableBodyCard>
        </TableContainer>
        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={handlePagination}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />
      </PageCard>

      {loading && <BackdropCustom />}
      <OrderConfirmDialog open={isConfirmDialogOpen} confirm={handleConfirmDialog} deny={handleDenyDialog} />
      {originalOrderId && originalOrderTypeId && (
        <OrderCloneDialog
          orderId={originalOrderId}
          open={isCloneDialogOpen}
          cloneOrder={handleCloneOrder}
          orderTypeId={originalOrderTypeId}
          deny={() => {
            setIsCloneDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

export { OrdersList };
